/**
 * Local-gateway DICOM source selection for the viewer.
 *
 * WebApi returns both a cloud `storageLocation` and (optionally) a LAN
 * `localStorageLocation` for each image/thumbnail — a short-lived, HMAC-signed
 * capability URL served by the clinic's onsite gateway over the LAN. This
 * module decides which URL the browser actually uses:
 *
 *  - `probeLocalGateway` / `ensureLocalGatewayProbed` find out whether this
 *    browser can reach the gateway's unauthenticated `/healthz` at all. A
 *    success holds for the tab; a failure expires on a backoff (./probeBackoff)
 *    and the next call after that — a study opening, a worklist poll, an image
 *    being dispatched — probes again, as does the browser reporting a network
 *    change. The probe is permission-aware: Chrome's
 *    (and Firefox's) Local Network Access permission gate holds any fetch to
 *    a private-network origin until the user answers a one-time per-origin
 *    prompt, so a naive short-timeout probe races that prompt and always
 *    loses — the abort fires before a human can click anything, the gateway
 *    gets recorded unreachable for the rest of the session, and the
 *    permission is left at `prompt` forever (it recurs every session, and the
 *    feature never self-engages in any browser). To avoid that, the verdict
 *    is driven off `navigator.permissions.query`, not just the fetch:
 *      - `granted`, or the Permissions API unsupported/throwing -> probe
 *        exactly as before: one `/healthz` fetch with a short timeout.
 *      - `denied` -> resolve unreachable immediately, with no fetch at all
 *        (there's nothing to wait for), but keep listening for the
 *        permission's `change` event in case the user grants it later.
 *      - `prompt` -> issue the probe anyway, but with a long (10-minute)
 *        deadline instead of the short one, so it survives a human actually
 *        reading and answering the browser's prompt, and also subscribe to
 *        `change`.
 *    Either way, granting the permission does **not** release an
 *    already-held fetch — it kills it (observed live as the fetch rejecting
 *    with "Failed to fetch") — so on a `change` to `granted` this module
 *    aborts whatever probe is in flight for that origin and immediately
 *    re-probes fresh; the retry lands in milliseconds once the origin is
 *    actually reachable. The same "a fresh fetch right after a grant
 *    succeeds where the held one just died" logic also guards a plain fetch
 *    rejection with no `change` event in sight: if the permission already
 *    reads `granted` by the time the rejection is caught, one fresh retry
 *    runs before the outcome is recorded as unreachable. A verdict that
 *    settles well into the session — the human took a minute to click
 *    "Allow" — still reaches `chooseStorageLocation` / ./registerFallback
 *    correctly, because both read the cached verdict at the moment they're
 *    asked, not at probe-start time.
 *  - `chooseStorageLocation` prefers the local URL once its origin has probed
 *    reachable, otherwise returns the cloud URL — so an off-site browser never
 *    attempts (or waits on) a LAN request beyond the one-time probe. This is the
 *    right call for anything that fetches at the moment it is asked (an `<img>`
 *    src, the video path), because "now" is also when the bytes are requested.
 *  - `imageIdSourceUrl` is the counterpart for Cornerstone imageIds, which are
 *    built once during render and then used as cache/identity keys. It always
 *    returns the local URL when one exists; the reachability decision is
 *    deferred to ./registerFallback, which rewrites the request as it is
 *    dispatched. See its doc comment for why the two differ.
 *  - `noteLocalOutcome` is a per-origin circuit breaker: after a few
 *    consecutive local failures it disables that origin for the rest of the
 *    session, so a gateway that goes down mid-session doesn't cost every
 *    remaining image a failed LAN attempt. Outcomes from the Cornerstone path
 *    come in through `noteDispatchedOutcome`, which counts only the requests
 *    ./registerFallback actually sent to the LAN — it records each one's real
 *    destination with `noteDispatchedSource` as it goes out, because an imageId
 *    names the LAN URL even when the bytes came from the cloud.
 *  - `withLocalFallback` retries a failed local load against the cloud URL
 *    once, for callers (e.g. the video path) that fetch bytes directly rather
 *    than going through the Cornerstone/wadouri pipeline. The force-local
 *    override turns that retry off with every other switch to the cloud.
 *
 * The Cornerstone-specific wiring (choosing the source for each wadouri request
 * as it is dispatched) lives in ./registerFallback, kept out of this file so
 * that importing `imageIdSourceUrl` — a plain pure function used from
 * imageIds.ts — doesn't drag `@cornerstonejs/dicom-image-loader` into every
 * consumer's dependency graph (that module's own imports reach back into
 * `@cornerstonejs/core` in ways that break unrelated tests mocking it).
 *
 * All state here is an in-memory, per-tab singleton — "session" means "this
 * browser tab's lifetime", not anything persisted. `resetLocalGatewayState`
 * exists only for tests; `retryLocalGateway` is its origin-scoped, production
 * counterpart, behind the viewer's retry control.
 *
 * The reachability verdict itself lives in ./status, which is observable, so the
 * viewer and worklist chrome can show which path a window is actually using
 * instead of every failure mode presenting as "it feels slow". ./status also
 * owns the support-facing force-local / force-cloud override, honoured here in
 * exactly one place: `isLocalEnabled`, which every source decision goes through.
 */
import { probeRetryDelayMs } from './probeBackoff';
import { clearLocalGatewayStatus, getDiagnosticOverride, getOriginPhase, knownLocalGatewayOrigins, setOriginPhase, } from './status';
const PROBE_TIMEOUT_MS = 1500;
// The Local Network Access prompt sits in front of the fetch while state is
// `prompt` — long enough for a human to notice the browser chrome and answer
// it, unlike the short timeout above, which exists only to bound a probe that
// has no permission gate to wait on.
const PROMPT_PROBE_TIMEOUT_MS = 10 * 60 * 1000;
const MAX_LOCAL_FAILURES = 3;
const LOCAL_NETWORK_PERMISSION_NAMES = [
    'local-network-access',
    'local-network',
];
// Session-scoped state (module singletons; cleared only by resetLocalGatewayState in tests).
// The per-origin *verdict* is not here — it lives in ./status as an OriginPhase,
// so that one map serves both the data path and the indicator and the two can
// never disagree about whether the gateway is usable.
const probePromises = new Map();
const failureCounts = new Map();
// Local URL (no wadouri scheme, includes query) -> cloud URL, populated as
// chooseStorageLocation picks local URLs. Lets a later failure (or a
// not-yet-dispatched request to a since-disabled origin) find the byte-identical
// cloud object without the caller having to carry both URLs around.
const localToCloud = new Map();
// The AbortController backing whichever /healthz fetch is currently in flight
// for an origin, so a permission `change` event can kill it (see
// subscribeToPermissionChange) the same way the browser itself does on grant.
const activeProbes = new Map();
// How many probes this window has started for an origin. A probe captures the
// count current when it began and re-checks it before every shared-state write,
// because a controller-identity check alone cannot cover the whole probe: a
// probe is suspended awaiting `queryLocalNetworkPermission` BEFORE its
// AbortController exists, so a retry has nothing to abort, and that suspended
// continuation would otherwise wake up to install a fetch, a permission
// listener and a verdict over the top of the fresh probe the user asked for.
const probeGenerations = new Map();
// Unsubscribe callbacks for the PermissionStatus `change` listeners this
// module has attached, keyed by origin.
const permissionUnsubscribers = new Map();
// Request URL (frame suffix stripped) -> the origin that request's bytes were
// actually fetched from, recorded by ./registerFallback as it dispatches. The
// phase at failure time does not prove which origin served a request: while a
// probe is in flight the dispatch goes to the cloud, and the probe can settle
// `reachable` before that cloud request fails. Attributing it to the LAN would
// trip the breaker on a node that has served nothing.
const dispatchedOrigins = new Map();
// When an origin whose probe failed may be probed again (epoch ms), and how many
// probes of it have failed in a row. Absent while nothing is due: the origin is
// reachable, the browser is refusing or still asking, or a re-probe is in flight.
const reprobeDueAt = new Map();
const consecutiveProbeFailures = new Map();
// Origins with an automatic re-probe in flight, so neither the backoff nor a
// burst of network-change events can stack a second one on top.
const reprobesInFlight = new Set();
// When a network change last re-probed each origin (epoch ms). Network events
// are bursty, and a re-probe that restarted the backoff every time would defeat it.
const lastNetworkChangeProbeAt = new Map();
const NETWORK_CHANGE_REPROBE_MIN_INTERVAL_MS = 30_000;
// Detaches the `online` / connection-change listeners; null until the first
// probe installs them.
let removeNetworkChangeListeners = null;
/** Start a new probe lineage for `origin` and return its generation. */
function beginProbeGeneration(origin) {
    const generation = (probeGenerations.get(origin) ?? 0) + 1;
    probeGenerations.set(origin, generation);
    return generation;
}
/** False once a retry (or a reset) has superseded the probe holding `generation`. */
function isCurrentProbeGeneration(origin, generation) {
    return probeGenerations.get(origin) === generation;
}
export function resetLocalGatewayState() {
    // Abort every in-flight /healthz fetch before dropping activeProbes — a
    // stale probe (especially one held for up to PROMPT_PROBE_TIMEOUT_MS behind
    // the permission prompt) would otherwise keep running past the reset and
    // eventually write its verdict into the phase map a later test is reading.
    // fetchProbe's own isCurrentProbe() guard is the other half of this: it stops
    // that write-back even if the abort itself doesn't land in time.
    for (const controller of activeProbes.values())
        controller.abort();
    probePromises.clear();
    failureCounts.clear();
    localToCloud.clear();
    activeProbes.clear();
    dispatchedOrigins.clear();
    // Every generation a suspended probe could be holding is now stale, which is
    // what stops one that was waiting on the permission query from settling into
    // the state a later test (or a later probe) is reading.
    probeGenerations.clear();
    for (const unsubscribe of permissionUnsubscribers.values())
        unsubscribe();
    permissionUnsubscribers.clear();
    reprobeDueAt.clear();
    consecutiveProbeFailures.clear();
    reprobesInFlight.clear();
    lastNetworkChangeProbeAt.clear();
    removeNetworkChangeListeners?.();
    removeNetworkChangeListeners = null;
    clearLocalGatewayStatus();
}
/**
 * Throw away everything this window believes about one gateway origin and probe
 * it again from scratch — the production retry behind the viewer's indicator.
 *
 * This is the only way back out of the circuit breaker without a page reload:
 * `noteLocalOutcome` disables an origin permanently (for the tab) after three
 * consecutive failures, which is right for a flapping gateway and wrong for a
 * gateway someone has just finished fixing.
 *
 * Aborting the in-flight probe first matters: a probe held behind Chrome's
 * Local Network Access prompt can be sitting on a ten-minute deadline, and
 * `fetchProbe`'s own isCurrentProbe() guard then stops its late settlement from
 * clobbering the fresh verdict. A probe that has not reached its fetch yet has
 * no controller to abort at all — it is suspended on the permission query — so
 * the probe generation bumped here is what stops that one: every continuation of
 * the replaced probe sees a newer generation and writes nothing.
 *
 * It cannot re-open a `permission-denied` origin by itself — once Chrome has
 * been told no, attempting another local fetch does not re-prompt (see
 * `startProbe`). The indicator says so, and points at the browser setting.
 */
export function retryLocalGateway(origin) {
    if (!origin)
        return Promise.resolve(false);
    activeProbes.get(origin)?.abort();
    activeProbes.delete(origin);
    probePromises.delete(origin);
    failureCounts.delete(origin);
    reprobeDueAt.delete(origin);
    consecutiveProbeFailures.delete(origin);
    reprobesInFlight.delete(origin);
    permissionUnsubscribers.get(origin)?.();
    permissionUnsubscribers.delete(origin);
    // Straight to `probing` rather than forgetting the origin first: dropping it
    // would make `origins: []` -> state `cloud` observable for one tick, which
    // unmounts the retry control the click came from (React's batching is the
    // only thing hiding that today).
    setOriginPhase(origin, 'probing');
    return probeLocalGateway(origin);
}
/** Retry every gateway origin this window knows about. */
export async function retryLocalGatewayProbes() {
    await Promise.all(knownLocalGatewayOrigins().map(retryLocalGateway));
}
export function originOf(url) {
    try {
        return new URL(url).origin;
    }
    catch {
        return '';
    }
}
/**
 * The multi-frame suffix that imageId expansion appends to a stored object's URL
 * (`?frame=2` / `&frame=2`), as a capture of the base URL and the suffix.
 *
 * The loader dispatches one request per stored object, while the Cornerstone
 * load events name the per-frame imageId — so anything keyed by a request URL
 * has to reduce both to the same key.
 */
const FRAME_SUFFIX = /^(.*?)([?&]frame=\d+)$/;
function withoutFrameSuffix(url) {
    return url.match(FRAME_SUFFIX)?.[1] ?? url;
}
/**
 * TS's DOM lib declares `Navigator.permissions` as always present, but real
 * browsers without a Permissions API at all (and jsdom, in tests) genuinely
 * leave it undefined — hence casting through `unknown` rather than trusting
 * the declared type, both here and for the two experimental permission names
 * `PermissionName` doesn't include.
 */
function getLocalNetworkPermissionsApi() {
    return navigator.permissions;
}
/**
 * Query the Local Network Access permission, trying each known name in turn.
 * Returns null when the Permissions API isn't available, or every name it
 * knows throws — callers treat null as "no permission gate; probe as usual."
 */
async function queryLocalNetworkPermission() {
    const permissions = getLocalNetworkPermissionsApi();
    if (!permissions)
        return null;
    for (const name of LOCAL_NETWORK_PERMISSION_NAMES) {
        try {
            return await permissions.query({ name });
        }
        catch {
            // Unknown permission name in this browser — try the next one.
        }
    }
    return null;
}
/**
 * Record a settled probe verdict.
 *
 * A negative verdict never overwrites `permission-denied`: the browser refusing
 * local network access is strictly more actionable than a bare "could not
 * reach" (change a Chrome setting, versus reload and hope), and the two arrive
 * in the wrong order — a permission flipping to `denied` kills the held fetch,
 * so the rejection lands *after* we already know why.
 */
function recordProbeVerdict(origin, ok) {
    if (ok) {
        reprobeDueAt.delete(origin);
        consecutiveProbeFailures.delete(origin);
        setOriginPhase(origin, 'reachable');
        return;
    }
    if (getOriginPhase(origin) === 'permission-denied')
        return;
    setOriginPhase(origin, 'unreachable');
    // Not the verdict for the rest of the tab: see ./probeBackoff for why, and
    // isReprobeDue for what acts on this.
    const failures = consecutiveProbeFailures.get(origin) ?? 0;
    consecutiveProbeFailures.set(origin, failures + 1);
    reprobeDueAt.set(origin, Date.now() + probeRetryDelayMs(failures));
}
/**
 * True when `origin`'s failed probe has expired and nothing is re-probing it.
 * Only `unreachable` expires: `permission-denied` is the browser refusing, which
 * no number of probes changes (the permission's `change` event is what
 * re-opens it), and `blocked` is the circuit breaker, whose way back is the
 * explicit retry or a network change.
 */
function isReprobeDue(origin) {
    if (getOriginPhase(origin) !== 'unreachable' || reprobesInFlight.has(origin))
        return false;
    const dueAt = reprobeDueAt.get(origin);
    return dueAt !== undefined && Date.now() >= dueAt;
}
/**
 * Probe an origin that already has a negative verdict again, without the
 * indicator going back to "checking" in between — the verdict stands until the
 * new one is in, so a gateway that stays down does not flash the chrome every
 * few minutes.
 *
 * Asks the browser first. Still `prompt` means the user has not answered Local
 * Network Access yet, and a fetch would only put the prompt up again (the grant,
 * when it comes, re-probes through the `change` listener re-attached here);
 * `denied` means no fetch can succeed. Either way nothing is fetched and no
 * further automatic re-probe is scheduled. A retry or reset that starts a new
 * probe lineage while this is waiting on the browser wins, and this stands down.
 */
function reprobe(origin) {
    reprobeDueAt.delete(origin);
    reprobesInFlight.add(origin);
    const lineage = probeGenerations.get(origin) ?? 0;
    const promise = (async () => {
        try {
            const status = getLocalNetworkPermissionsApi() ? await queryLocalNetworkPermission() : null;
            if ((probeGenerations.get(origin) ?? 0) !== lineage)
                return false;
            if (status && status.state !== 'granted') {
                subscribeToPermissionChange(origin, status, lineage);
                if (status.state === 'denied')
                    setOriginPhase(origin, 'permission-denied');
                return false;
            }
            // A fresh verdict starts the breaker over, as the explicit retry does.
            failureCounts.delete(origin);
            return await fetchProbe(origin, PROBE_TIMEOUT_MS, beginProbeGeneration(origin));
        }
        finally {
            reprobesInFlight.delete(origin);
        }
    })();
    probePromises.set(origin, promise);
    return promise;
}
/**
 * Called as the Cornerstone loader dispatches a request it is sending to the
 * cloud because `origin` is not usable: if that origin's failed probe has
 * expired, probe it again in the background. The request itself still goes to
 * the cloud — nothing waits on this — but the ones dispatched after a success
 * go to the LAN, so a long-open viewer recovers without a new study being opened.
 */
export function reprobeLocalGatewayIfDue(origin) {
    if (origin && isReprobeDue(origin))
        void reprobe(origin);
}
/**
 * The browser says its network changed (back online, or a different
 * connection): every origin this window gave up on gets one probe now, with its
 * backoff started over. A laptop that has just moved networks is the case the
 * expiry exists for, and this is the earliest moment to look — though the
 * gateway's name may still point at the old address for a minute or two, which
 * is what the backoff that follows a failure here covers.
 *
 * `blocked` origins are included, unlike for the timed expiry: the breaker
 * tripped on loads that failed on the network this browser has just left.
 */
function reprobeAfterNetworkChange() {
    for (const origin of knownLocalGatewayOrigins()) {
        const phase = getOriginPhase(origin);
        if (phase !== 'unreachable' && phase !== 'blocked')
            continue;
        if (reprobesInFlight.has(origin))
            continue;
        // Events inside the window neither probe nor touch the failure count, so
        // the backoff a failing gateway has earned survives a chatty network stack.
        const now = Date.now();
        const last = lastNetworkChangeProbeAt.get(origin);
        if (last !== undefined && now - last < NETWORK_CHANGE_REPROBE_MIN_INTERVAL_MS)
            continue;
        lastNetworkChangeProbeAt.set(origin, now);
        consecutiveProbeFailures.delete(origin);
        void reprobe(origin);
    }
}
const connectionKind = (connection) => `${connection?.type ?? ''}/${connection?.effectiveType ?? ''}`;
/**
 * `online` fires on every browser; `navigator.connection`'s `change` (Network
 * Information API, Chromium only) also fires on a switch between two networks
 * with no offline gap in between, which is the more common shape of a laptop
 * moving from venue Wi-Fi to a phone hotspot. Installed with the first probe so
 * a window that never meets a gateway listens for nothing.
 */
function ensureNetworkChangeListeners() {
    if (removeNetworkChangeListeners || typeof window === 'undefined')
        return;
    const connection = navigator.connection;
    // Chromium also fires `change` when only its rtt / downlink estimates move,
    // which is not a different network; only a new connection type counts.
    let lastKind = connectionKind(connection);
    const onOnline = () => reprobeAfterNetworkChange();
    const onConnectionChange = () => {
        const kind = connectionKind(connection);
        if (kind === lastKind)
            return;
        lastKind = kind;
        reprobeAfterNetworkChange();
    };
    window.addEventListener('online', onOnline);
    connection?.addEventListener?.('change', onConnectionChange);
    removeNetworkChangeListeners = () => {
        window.removeEventListener('online', onOnline);
        connection?.removeEventListener?.('change', onConnectionChange);
    };
}
/**
 * Issue one `/healthz` fetch and record its verdict. `retriedAfterGrant`
 * guards against looping: it's only true on the one fresh retry a rejection
 * gets when the permission has just read `granted` (see the catch branch
 * below and subscribeToPermissionChange), so a genuinely-down gateway still
 * settles false after that single extra attempt instead of retrying forever.
 */
async function fetchProbe(origin, timeoutMs, generation, retriedAfterGrant = false) {
    // Checked before touching activeProbes: a superseded probe that got this far
    // would otherwise overwrite the current probe's controller and make *that* one
    // look like the stale attempt.
    if (!isCurrentProbeGeneration(origin, generation))
        return false;
    const controller = new AbortController();
    activeProbes.set(origin, controller);
    // Whether this specific attempt is still the one the module considers
    // current for `origin` — false once resetLocalGatewayState (a reset clears
    // activeProbes entirely) or a subsequent fetchProbe call for the same
    // origin (which overwrites the map entry) has superseded it. Guards every
    // shared-state write below so a stale attempt settling late — most likely
    // a probe aborted mid-reset while held behind the permission prompt — can't
    // clobber a newer verdict. It does not change this call's own return value:
    // whoever is awaiting *this* promise still learns its own outcome.
    const isCurrentProbe = () => activeProbes.get(origin) === controller && isCurrentProbeGeneration(origin, generation);
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const resp = await fetch(`${origin}/healthz`, { signal: controller.signal, mode: 'cors' });
        const ok = resp.ok;
        if (isCurrentProbe())
            recordProbeVerdict(origin, ok);
        return ok;
    }
    catch {
        // A grant kills any held fetch outright rather than releasing it, so a
        // rejection that lands just after the permission turned `granted` isn't
        // a real network failure — one fresh attempt now succeeds in
        // milliseconds. Anything else (still prompting, denied, or already
        // retried once) is a genuine failure.
        if (!retriedAfterGrant) {
            const status = await queryLocalNetworkPermission();
            if (status?.state === 'granted' && isCurrentProbe()) {
                return fetchProbe(origin, PROBE_TIMEOUT_MS, generation, true);
            }
        }
        if (isCurrentProbe())
            recordProbeVerdict(origin, false);
        return false;
    }
    finally {
        clearTimeout(timer);
        if (isCurrentProbe())
            activeProbes.delete(origin);
    }
}
/**
 * Listen for `status` (the origin's Local Network Access PermissionStatus)
 * to change while its state is `prompt` or `denied`. A flip to `granted`
 * either aborts the in-flight held probe (its own catch branch then re-probes
 * fresh — see fetchProbe) or, if nothing is in flight (the `denied` case
 * never issued a fetch to begin with), kicks a fresh probe directly. A flip
 * to `denied` aborts any in-flight probe and records unreachable.
 */
function subscribeToPermissionChange(origin, status, generation) {
    permissionUnsubscribers.get(origin)?.();
    const handleChange = () => {
        // A retry (or a reset) has replaced the probe this listener was installed
        // for: it now speaks for a verdict the window has already thrown away, and
        // the replacement probe has a listener of its own where one is needed.
        if (!isCurrentProbeGeneration(origin, generation))
            return;
        // Record a denial BEFORE touching the in-flight probe. Denying the
        // permission kills the held fetch, so its rejection lands after this
        // handler — and "the browser said no" (change a setting) must not be
        // downgraded to "could not reach" (reload and hope) by that late
        // rejection. recordProbeVerdict is what preserves it.
        if (status.state === 'denied')
            setOriginPhase(origin, 'permission-denied');
        const active = activeProbes.get(origin);
        if (active) {
            active.abort();
            return;
        }
        // A grant re-opens an origin the *browser* was refusing. It deliberately
        // does not re-open one the circuit breaker tripped: that origin failed
        // three real LAN loads while it was reachable, which no permission change
        // fixes, and re-probing would show the indicator green again until the next
        // failure turned it amber. `retryLocalGateway` is the way back from a trip.
        if (status.state === 'granted' && getOriginPhase(origin) !== 'blocked') {
            probePromises.delete(origin);
            // A fresh verdict starts the breaker over, exactly as the explicit retry
            // does — otherwise a stale count left by the pre-grant session trips on
            // the first failure after the re-probe.
            failureCounts.delete(origin);
            setOriginPhase(origin, 'probing');
            probePromises.set(origin, fetchProbe(origin, PROBE_TIMEOUT_MS, generation));
        }
    };
    status.addEventListener('change', handleChange);
    permissionUnsubscribers.set(origin, () => status.removeEventListener('change', handleChange));
}
async function startProbe(origin, generation) {
    const status = await queryLocalNetworkPermission();
    // The window started a fresh probe (or reset) while this one was suspended
    // here — before it had an AbortController for a retry to abort. Everything
    // below writes state shared with that fresh probe, so this continuation stops
    // instead: no fetch, no `change` listener, no verdict.
    if (!isCurrentProbeGeneration(origin, generation))
        return false;
    if (!status || status.state === 'granted') {
        return fetchProbe(origin, PROBE_TIMEOUT_MS, generation);
    }
    subscribeToPermissionChange(origin, status, generation);
    if (status.state === 'denied') {
        setOriginPhase(origin, 'permission-denied');
        return false;
    }
    // 'prompt'
    return fetchProbe(origin, PROMPT_PROBE_TIMEOUT_MS, generation);
}
/**
 * Probe a gateway origin's `/healthz`, caching the verdict — for the session
 * when it is a success, until it expires (isReprobeDue) when it is a failure.
 */
export function probeLocalGateway(origin) {
    if (!origin)
        return Promise.resolve(false);
    ensureNetworkChangeListeners();
    const cached = probePromises.get(origin);
    if (cached)
        return isReprobeDue(origin) ? reprobe(origin) : cached;
    // Recorded before the first await so the indicator shows "checking" from the
    // moment a study offers a LAN URL, not only once the permission query
    // resolves — and so the store knows this origin exists at all, which is what
    // separates "this clinic has no node" from "this clinic has one and it is
    // not answering".
    setOriginPhase(origin, 'probing');
    // Browsers with no Local Network Access gate at all (most of them, today)
    // dispatch the fetch in the same tick as before — no permission to ask
    // about, so no reason to make every probe pay an extra microtask hop for
    // one. Only origins on a browser that actually exposes the gate go through
    // startProbe's async `navigator.permissions.query` check.
    const generation = beginProbeGeneration(origin);
    const promise = getLocalNetworkPermissionsApi()
        ? startProbe(origin, generation)
        : fetchProbe(origin, PROBE_TIMEOUT_MS, generation);
    probePromises.set(origin, promise);
    return promise;
}
/** Probe every distinct gateway origin among the given local URLs, once each. */
export async function ensureLocalGatewayProbed(localUrls) {
    const origins = new Set();
    for (const url of localUrls) {
        if (url) {
            const origin = originOf(url);
            if (origin)
                origins.add(origin);
        }
    }
    await Promise.all([...origins].map(probeLocalGateway));
}
/**
 * True once `origin` has probed reachable and hasn't since tripped the circuit
 * breaker.
 *
 * Every local-versus-cloud decision in the app funnels through here —
 * `chooseStorageLocation`, `withLocalFallback`, and ./registerFallback's
 * dispatch-time rewrite — which is why the support override is applied at this
 * one point rather than at each of them. `force-local` deliberately ignores the
 * probe verdict and the breaker: the whole question support is answering is
 * "does the LAN path work from this machine", and refusing to try it is how
 * that question goes unanswered.
 */
export function isLocalEnabled(origin) {
    if (!origin)
        return false;
    const override = getDiagnosticOverride();
    if (override === 'force-local')
        return true;
    if (override === 'force-cloud')
        return false;
    return getOriginPhase(origin) === 'reachable';
}
/**
 * Record a local load outcome for `origin`, but only while this window is
 * actually reading from it. A success resets the consecutive-failure count;
 * after MAX_LOCAL_FAILURES consecutive failures the origin is disabled for the
 * rest of the session (isLocalEnabled starts returning false, regardless of
 * later successes — a flapping gateway isn't worth re-trying).
 * `retryLocalGateway` is the deliberate way back, and the indicator offers it.
 */
export function noteLocalOutcome(origin, ok) {
    if (!origin)
        return;
    // `reachable` is the only phase in which this window is actually reading from
    // the origin, so it is the only phase whose outcomes say anything about it.
    // This is defence in depth, not the primary guard: whether an outcome is
    // evidence about the LAN is settled by where its request was SENT, which for
    // the Cornerstone path is noteDispatchedOutcome's job and for the direct
    // loaders is plain (they fetch the URL they were handed). Counting an outcome
    // from any other phase would trip the breaker on a node nothing has tried
    // yet, and then flap, as the probe settling writes `reachable` straight back
    // over `blocked`. Once the breaker has tripped, or the browser has refused,
    // there is nothing left to learn either: the breaker stays tripped for the tab
    // and the refusal stays the reported reason, because it is the one with a fix
    // attached. `force-local` bypasses all of this in isLocalEnabled, which is the
    // point — support asked to keep hitting the LAN whatever the phase says.
    if (getOriginPhase(origin) !== 'reachable')
        return;
    if (ok) {
        failureCounts.set(origin, 0);
        return;
    }
    const next = (failureCounts.get(origin) ?? 0) + 1;
    failureCounts.set(origin, next);
    if (next < MAX_LOCAL_FAILURES)
        return;
    setOriginPhase(origin, 'blocked');
}
/**
 * Record which origin a request the Cornerstone loader is dispatching was
 * actually sent to. Called from ./registerFallback's dispatch-time rewrite,
 * the only place that knows: an imageId always names the LAN URL, and whether
 * the bytes come from there is decided per request, as it goes out.
 */
export function noteDispatchedSource(requestUrl, dispatchedUrl) {
    if (!requestUrl)
        return;
    dispatchedOrigins.set(withoutFrameSuffix(requestUrl), originOf(dispatchedUrl));
}
/**
 * Record a dispatched request's outcome against the origin that actually served
 * it — the counterpart to noteLocalOutcome for the Cornerstone path, where the
 * URL a failure names is not the URL that was fetched.
 *
 * Only a request this window sent to the LAN says anything about the LAN. One
 * the rewrite sent to the cloud (everything dispatched while the probe was in
 * flight) must not count, and the phase at failure time cannot tell them apart:
 * the probe can settle `reachable` before such a request fails, at which point
 * the failure looks local in every way except where it was sent. A request this
 * window never dispatched is not evidence either.
 */
export function noteDispatchedOutcome(requestUrl, ok) {
    const localOrigin = originOf(requestUrl);
    if (dispatchedOrigins.get(withoutFrameSuffix(requestUrl)) !== localOrigin)
        return;
    noteLocalOutcome(localOrigin, ok);
}
/** Pick the local URL when its origin is currently enabled, else the cloud URL. */
export function chooseStorageLocation(image) {
    const local = image.localStorageLocation;
    if (!local)
        return image.storageLocation;
    if (!isLocalEnabled(originOf(local)))
        return image.storageLocation;
    localToCloud.set(local, image.storageLocation);
    return local;
}
/**
 * The URL to bake into an image's Cornerstone imageId: the LAN URL whenever the
 * study has one, *regardless* of whether the gateway has probed reachable.
 *
 * Unlike chooseStorageLocation this is deliberately not gated on reachability,
 * which makes it a pure function of the DTO — the same image yields the same
 * imageId for the whole session. That matters because an imageId is an identity,
 * not just an address: Cornerstone's image and dataset caches, the prefetcher's
 * per-series registration, the imageId <-> SOP-instance maps behind annotation
 * save/restore and burn-in, and the display-metadata cache all compare imageIds
 * as exact strings. Deriving them from a value that changes when a background
 * probe settles means two callers that build ids for the same image at different
 * moments disagree, silently.
 *
 * The reachability decision moves to request-dispatch time instead — see
 * ./registerFallback, which rewrites the outgoing request to the cloud URL
 * unless the origin has *proven* reachable. A probe that is still in flight,
 * or that failed, therefore serves the bytes from the cloud; only the imageId
 * still reads "local". This is also what lets a study that renders once and
 * never again (a single-series study) still use the LAN: the probe settles
 * before most of the stack is dispatched, and no re-render is required.
 */
export function imageIdSourceUrl(image) {
    const local = image.localStorageLocation;
    if (!local)
        return image.storageLocation;
    localToCloud.set(local, image.storageLocation);
    return local;
}
/** True when two URLs denote the same resource, resolving relative ones first. */
function sameUrl(a, b) {
    if (a === b)
        return true;
    try {
        const base = globalThis.location?.href;
        return new URL(a, base).href === new URL(b, base).href;
    }
    catch {
        return false;
    }
}
/**
 * The URL an `<img>` should retry with after failing to load `failedSrc`, or
 * null when there is nothing left to try — `failedSrc` was already the cloud
 * URL, or the image has no cloud URL at all — in which case the caller should
 * fall back to its own placeholder.
 *
 * Plain `<img>` loads bypass the Cornerstone loader, so they can't use the
 * dispatch-time rewrite in ./registerFallback and need this per-element retry
 * instead. A failure here also counts against the origin's breaker, so a
 * gateway that goes down mid-session stops being tried at all.
 */
export function cloudRetryImageSrc(failedSrc, cloudUrl) {
    // `failedSrc` normally comes from img.src, which the DOM has resolved to an
    // absolute URL — compare like-for-like so a relative cloud URL can't retry
    // itself forever.
    if (!cloudUrl || sameUrl(failedSrc, cloudUrl))
        return null;
    noteLocalOutcome(originOf(failedSrc), false);
    return cloudUrl;
}
/**
 * Given a local URL (bare, or with a `?frame=`/`&frame=` suffix appended by
 * multi-frame imageId expansion), returns the tracked byte-identical cloud URL,
 * or undefined if `url` was never chosen via chooseStorageLocation. Exported
 * for ./registerFallback, which needs the same lookup keyed off a raw request
 * URL rather than a wadouri imageId.
 */
export function cloudUrlForLocalUrl(url) {
    const direct = localToCloud.get(url);
    if (direct)
        return direct;
    const frameSplit = url.match(FRAME_SUFFIX);
    if (frameSplit) {
        const [, base, frameParam] = frameSplit;
        const baseCloud = localToCloud.get(base);
        if (baseCloud) {
            const sep = baseCloud.includes('?') ? '&' : '?';
            return `${baseCloud}${sep}${frameParam.slice(1)}`;
        }
    }
    return undefined;
}
/**
 * Try `load(local-or-cloud URL)`, retrying once against the cloud URL if the
 * first attempt used a local URL and failed. For loaders that fetch bytes
 * directly (outside the Cornerstone/wadouri pipeline — e.g. the video path),
 * where the caller controls the whole request and a plain retry is enough to
 * guarantee a LAN failure never surfaces as a broken load.
 */
export async function withLocalFallback(image, load) {
    const chosen = chooseStorageLocation(image);
    if (chosen === image.storageLocation)
        return load(chosen);
    try {
        const result = await load(chosen);
        noteLocalOutcome(originOf(chosen), true);
        return result;
    }
    catch (error) {
        noteLocalOutcome(originOf(chosen), false);
        // `force-local` turns this fallback off, as it does every other switch to
        // the cloud. The question support is answering with it is "does the LAN path
        // work from this machine", and quietly serving the bytes from the cloud
        // answers it wrongly while the indicator still says the onsite node is
        // pinned. The control's own copy warns that images may fail to load.
        if (getDiagnosticOverride() === 'force-local')
            throw error;
        return load(image.storageLocation);
    }
}
